import ReactGA from 'react-ga4';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
/////////////////////////////////////////////////////////////
import { telegramWebApp } from '@/main';
import { useCustomDispatch, useCustomSelector } from '@/core/redux/hooks';
import {
	Drawer,
	DrawerContent,
	DrawerClose,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	Button,
	Close,
	DrawerDescription,
} from '@/app/shared/components';
import {
	setCounter,
	setDiamonds,
	setStickerWonDialog,
	setTotalCoinsGenerated,
} from '@/core/redux/slices';

import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useState } from 'react';
import { HackService } from '@/core/services';
import { numify } from 'numify';
import { ExchangeDiamondsConfirmed } from '../ExchangeDiamondsConfirmed';
import { setVisibleModalInsufficientGems } from '@/core/redux/slices';

interface ExchangeDiamondsProps {
	isOpen: boolean;
	onClose: React.Dispatch<React.SetStateAction<boolean>>;
	image: string;
}

export const ExchangeDiamonds: React.FC<ExchangeDiamondsProps> = ({
	isOpen,
	onClose,
	image,
}: ExchangeDiamondsProps) => {
	/*********************
	// #region Hooks
	**********************/
	const dispatch = useCustomDispatch();
	const { t } = useTranslation();

	/*********************
	// #region Redux
	**********************/
	const { diamonds } = useCustomSelector((state) => state.counter);
	const { exchangeDiamondsRating } = useCustomSelector(
		(state) => state.hacks
	);

	// console.log(exchangeDiamondsRating);
	/*********************
	// #region Functions
	**********************/
	type Marks = {
		[key: number]: {
			style: {
				color: string;
				fontWeight: string;
			};
			label: string;
		};
	};

	const marks = exchangeDiamondsRating.reduce<Marks>((acc, item) => {
		acc[item.costDiamonds] = {
			style: {
				color: '#fff',
				fontWeight: 'bold',
			},
			label: numify(item.receiveCoins).toString(),
		};
		return acc;
	}, {});

	const handleRequestSwap = async () => {
		telegramWebApp.HapticFeedback.impactOccurred('heavy');
		if (diamonds < selectedValue) {
			toast.warning(`${t('hacks.exchangeDiamonds.insufficient')}`);
			dispatch(setVisibleModalInsufficientGems(true));
			return;
		}

		toast.promise(HackService.exchangeShurikens(selectedValue), {
			loading: t('general.loading'),
			success: (data: any) => {
				// Emit event to Google Analytics
				ReactGA.event({
					category: 'boosts',
					action: 'Exchange diamonds hack',
				});

				// Update counter
				dispatch(setCounter(data.currentCoins));
				// Update diamonds
				dispatch(setDiamonds(data.currentDiamonds));
				// Set total coins
				dispatch(setTotalCoinsGenerated(data.totalCoinsEarned));
				// Check if has a gift
				if (data.isAwardWinning) {
					dispatch(setStickerWonDialog(true));
				}

				setVisibleSuccessExangeDialog(true);

				setValueRequested(marks[selectedValue].label);

				return t('hacks.exchangeDiamonds.exchangeConfirm');
			},
			error: t('hacks.exchangeDiamonds.exchangeError'),
		});
		onClose(false);
	};

	/*********************
	// #region States
	**********************/
	const [selectedValue, setSelectedValue] = useState(
		Math.min(...Object.keys(marks).map(Number))
	);
	const [valueRequested, setValueRequested] = useState<string>();
	const [visibleSuccessExangeDialog, setVisibleSuccessExangeDialog] =
		useState<boolean>(false);

	/*********************
	// #region Template
	**********************/
	return (
		<>
			<Drawer open={isOpen} onOpenChange={onClose}>
				<DrawerContent>
					<div className="mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" />
					<div className="mx-auto w-full max-w-sm ">
						<DrawerHeader>
							<DrawerTitle>
								<VisuallyHidden>
									{t('hacks.exchangeDiamonds.title')}
								</VisuallyHidden>
								<DrawerClose className="right-5 text-right absolute">
									<Close fillcolor="#fff" />
								</DrawerClose>
							</DrawerTitle>
						</DrawerHeader>

						<DrawerDescription>
							<VisuallyHidden>
								{t('hacks.exchangeDiamonds.description')}
							</VisuallyHidden>
						</DrawerDescription>

						<div className="mx-auto px-8">
							<img
								className="cursor-pointer mx-auto w-[100px] mb-6"
								src={image}
							/>
							<p className="font-bold text-2xl text-center">
								{t('hacks.exchangeDiamonds.title')}
							</p>
							<div className="my-6 mx-8 px-2">
								<Slider
									min={Math.min(
										...Object.keys(marks).map(Number)
									)}
									max={Math.max(
										...Object.keys(marks).map(Number)
									)}
									defaultValue={Math.min(
										...Object.keys(marks).map(Number)
									)}
									step={null}
									included={false}
									dots={true}
									onChange={(value: any) => {
										setSelectedValue(value);
									}}
									marks={marks}
									styles={{
										track: {
											backgroundColor: '#00E0FF',
											marginTop: '-4px',
										},
										rail: {
											backgroundColor: '#0071BC',
											height: '1px',
											marginTop: '-4px',
										},
										handle: {
											marginTop: '-15px',
											backgroundColor: 'yellow',
											height: '24px',
											width: '24px',
										},
									}}
									dotStyle={{
										backgroundColor: '#0071BC',
										height: '16px',
										width: '16px',
										border: 'none',
									}}
									activeDotStyle={{
										backgroundColor: 'yellow',
										height: '25px',
										width: '25px',
										border: 'none',
										marginTop: '10px',
									}}
								/>
							</div>

							<div className="flex flex-col gap-3 mt-10 mb-4">
								<p className="text-center text-sm">
									{t('hacks.exchangeDiamonds.text')}
								</p>
							</div>
						</div>

						<DrawerFooter>
							<DrawerClose asChild>
								<Button
									variant="primary"
									className="py-6 w-full text-sm rounded-lg flex gap-2 items-center"
									onClick={handleRequestSwap}
								>
									<div className="flex justify-center gap-1 items-center ">
										<p className="text-center font-bold leading-none">
											{t('hacks.exchangeDiamonds.swap')}{' '}
											{selectedValue}
										</p>
										<img
											src={
												process.env
													.VITE_MINI_DIAMOND_IMAGE
											}
											alt={t(
												'hacks.exchangeDiamonds.text'
											)}
											className="w-4 h-4"
										/>
									</div>
								</Button>
							</DrawerClose>
						</DrawerFooter>
					</div>
				</DrawerContent>
			</Drawer>
			{visibleSuccessExangeDialog && valueRequested && (
				<ExchangeDiamondsConfirmed
					isOpen={visibleSuccessExangeDialog}
					onClose={setVisibleSuccessExangeDialog}
					shurikens={valueRequested}
					diamonds={selectedValue}
				/>
			)}
		</>
	);
};
